export const formatAgreementBlockLabel = (block) => {
  const name = block?.block?.name || block?.name || "Блок";
  const floors = Number(block?.floor_count);
  if (Number.isFinite(floors) && floors > 0) {
    return `${name} (${floors} эт.)`;
  }
  return name;
};

export const getAgreementBlockPlanCost = (block) =>
  (Number(block?.money_expenditures) || 0) +
  (Number(block?.bonus_money_expenditures) || 0);

/** Плановая стоимость раздела — та же логика, что в смете (WorkTab). */
export const getAgreementSectionPlanCost = (section, blocks = []) => {
  if (section?.amount != null) return Number(section.amount) || 0;
  if (blocks.length > 0) {
    return blocks.reduce(
      (sum, block) => sum + getAgreementBlockPlanCost(block),
      0,
    );
  }
  return Number(section?.money_expenditures?.planned) || 0;
};

export const PAYMENT_TEMPLATE_PERCENTAGES = {
  "30_50_20": [30, 50, 20],
  "50_50": [50, 0, 50],
  "70_30": [70, 0, 30],
  "80_20": [80, 0, 20],
  "100": [100, 0, 0],
};

export const BASE_PAYMENT_STAGES_COUNT = 3;
export const MAX_PAYMENT_STAGES_COUNT = 10;

/**
 * Нормализует проценты в массив [аванс, ОДГЭ, ОПГЭ, ...доп. этапы].
 * Минимум 3 элемента, дополнительные этапы сохраняются.
 */
export const normalizePaymentPercentages = (percentages) => {
  const source = Array.isArray(percentages) ? percentages : [];
  const length = Math.min(
    MAX_PAYMENT_STAGES_COUNT,
    Math.max(BASE_PAYMENT_STAGES_COUNT, source.length),
  );
  return Array.from({ length }, (_, index) => {
    const value = Number(source[index]);
    return Number.isFinite(value) ? value : 0;
  });
};

/** Убирает нулевые дополнительные этапы в конце (базовые 3 позиции сохраняются). */
export const trimPaymentPercentages = (percentages) => {
  const normalized = normalizePaymentPercentages(percentages);
  let length = normalized.length;
  while (length > BASE_PAYMENT_STAGES_COUNT && normalized[length - 1] === 0) {
    length -= 1;
  }
  return normalized.slice(0, length);
};

const isApiDateOnlyString = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Парсит YYYY-MM-DD как локальную календарную дату (без сдвига UTC). */
export const parseApiDateValue = (value) => {
  if (!value) return null;
  if (isApiDateOnlyString(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

/** Форматирует Date в YYYY-MM-DD по локальному календарю (без toISOString). */
export const formatDateForApi = (date = new Date()) => {
  const d =
    date instanceof Date ? date : parseApiDateValue(date) ?? new Date(date);
  if (!d || Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const resolvePaymentTemplateFromPercentages = (percentages) => {
  if (!Array.isArray(percentages)) return "30_50_20";
  const normalized = trimPaymentPercentages(percentages);
  const match = Object.entries(PAYMENT_TEMPLATE_PERCENTAGES).find(
    ([, template]) =>
      template.length === normalized.length &&
      template.every((value, index) => value === normalized[index]),
  );
  return match?.[0] ?? "custom";
};

/** Категория контрагента для радиокнопок типа исполнителя. */
export const getCounterpartyLegalCategory = (item) => {
  if (!item) return null;
  const legalForm = String(item.legal_form || "").trim().toLowerCase();
  const entityType = String(
    item.type || item.counterparty_type || "",
  )
    .trim()
    .toLowerCase();
  const isNdsPayer = Boolean(item.is_nds_payer);

  if (legalForm.includes("гпх") || legalForm.includes("физическое лицо")) {
    return "individual_gph";
  }
  if (legalForm === "ип" || legalForm.startsWith("ип ")) {
    return "ip";
  }
  if (legalForm.includes("тоо") || legalForm.includes("llp")) {
    return isNdsPayer ? "too_with_vat" : "too_without_vat";
  }
  if (entityType === "physical_entity") {
    return "ip";
  }
  return isNdsPayer ? "too_with_vat" : "too_without_vat";
};

export const matchesPerformerType = (item, performerType) =>
  getCounterpartyLegalCategory(item) === performerType;

export const mapCounterpartyToUiPerformerType = (counterparty) => {
  if (!counterparty) return "too_with_vat";
  return getCounterpartyLegalCategory(counterparty) || "too_with_vat";
};

export const resolveUiPerformerTypeFromAgreement = async (
  agreement,
  getAllCounterparties,
) => {
  const apiType = agreement?.performer_type;
  if (apiType === "contract_employee") return "contract_employee";
  if (apiType === "gph_physical") return "individual_gph";
  if (agreement?.counterparty_id == null) return "too_with_vat";

  try {
    const list = await getAllCounterparties({
      counterparty_side: "counterparty",
    });
    const counterparty = (Array.isArray(list) ? list : []).find(
      (item) => Number(item.id) === Number(agreement.counterparty_id),
    );
    return mapCounterpartyToUiPerformerType(counterparty);
  } catch {
    return "too_with_vat";
  }
};

export const buildAgreementBlocksState = (agreement, sectionsData) => {
  const sectionIds = Array.isArray(agreement?.section_ids)
    ? agreement.section_ids.map(Number).filter((id) => Number.isFinite(id))
    : [];

  const blockPrices = new Map();
  [
    ...(agreement?.agreement_blocks_detail || []),
    ...(agreement?.agreement_blocks || []),
  ].forEach((item) => {
    if (item?.block_id != null) {
      blockPrices.set(Number(item.block_id), Number(item.price) || 0);
    }
  });

  const sectionPriceById = new Map();
  (Array.isArray(agreement?.section_prices) ? agreement.section_prices : []).forEach(
    (item) => {
      const pricedSectionId = Number(item?.section_id);
      if (!Number.isFinite(pricedSectionId)) return;
      sectionPriceById.set(pricedSectionId, Number(item?.price) || 0);
    },
  );

  const selectedSections = new Set(sectionIds);
  const selectedBlocks = {};
  const agreementCosts = {};

  sectionIds.forEach((sectionId) => {
    const section = sectionsData[sectionId];
    const blocks = section?.block_expenditures || [];

    if (blocks.length === 0) {
      if (sectionPriceById.has(sectionId)) {
        agreementCosts[sectionId] = {
          section_total: sectionPriceById.get(sectionId),
        };
      } else if (sectionIds.length === 1 && sectionPriceById.size === 0) {
        agreementCosts[sectionId] = {
          section_total: Number(agreement?.price) || 0,
        };
      }
      return;
    }

    selectedBlocks[sectionId] = {};
    agreementCosts[sectionId] = {};

    blocks.forEach((block, blockIndex) => {
      const blockId = Number(block?.id ?? block?.block?.id);
      const price = blockPrices.get(blockId);
      if (price != null && price > 0) {
        selectedBlocks[sectionId][blockIndex] = true;
        agreementCosts[sectionId][blockIndex] = price;
      }
    });
  });

  return { selectedSections, selectedBlocks, agreementCosts };
};

export const buildAgreementBlocksPayload = (
  selectedSections,
  sectionsData,
  agreementCosts,
) => {
  const blocks = [];
  const blockIds = [];
  const sectionPrices = [];

  Array.from(selectedSections).forEach((sectionId) => {
    const section = sectionsData[sectionId];
    const blockExpenditures = section?.block_expenditures || [];
    const sectionCosts = agreementCosts[sectionId] || {};
    const numericSectionId = Number(sectionId);

    if (blockExpenditures.length === 0) {
      const price = Number(sectionCosts.section_total);
      if (!Number.isFinite(numericSectionId) || !price) return;
      sectionPrices.push({
        section_id: numericSectionId,
        price: Math.round(price),
      });
      return;
    }

    Object.keys(sectionCosts).forEach((blockIndexStr) => {
      if (blockIndexStr === "section_total") return;
      const blockIndex = Number(blockIndexStr);
      if (!Number.isFinite(blockIndex)) return;
      const cost = Number(sectionCosts[blockIndexStr]);
      if (!cost) return;
      const block = blockExpenditures[blockIndex];
      const blockId = block?.id ?? block?.block?.id;
      if (blockId == null) return;
      blockIds.push(Number(blockId));
      blocks.push({ block_id: Number(blockId), price: Math.round(cost) });
    });
  });

  return { block_ids: blockIds, blocks, section_prices: sectionPrices };
};
